import { useState, useCallback, useEffect, useRef } from 'react';

const META_APP_ID = process.env.REACT_APP_META_APP_ID;
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID;

let fbInitialized = false;

export default function useFacebookSDK() {
  const [sdkReady, setSdkReady] = useState(fbInitialized);
  const [loading, setLoading] = useState(false);
  const sessionInfoRef = useRef(null);

  // Listen for Embedded Signup message events from Meta popup
  useEffect(() => {
    const handler = (event) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // Meta emite vários eventos durante o flow (PARTNER_ADD, WABA_ADD,
          // PHONE_NUMBER_ADD, FINISH, CANCEL). Só persistimos sessionInfo no
          // FINISH — eventos intermediários têm payload parcial e sobrescrever
          // com eles causa o FB.login retornar phone_number_id vazio.
          if (data.event === 'FINISH') {
            sessionInfoRef.current = data.data;
          } else if (data.event === 'CANCEL') {
            sessionInfoRef.current = null;
          } else {
            console.debug('[CLOUD-API] Evento intermediário ignorado:', data.event);
          }
        }
      } catch (err) {
        console.debug('[CLOUD-API] Mensagem postMessage ignorada (não-JSON):', err?.message);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const loadSDK = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!META_APP_ID) {
        const err = new Error(
          'REACT_APP_META_APP_ID nao definido no .env. Configure o App ID do Meta Developer Portal.'
        );
        console.error('[CLOUD-API]', err.message);
        reject(err);
        return;
      }

      const doInit = () => {
        if (!fbInitialized) {
          window.FB.init({
            appId: META_APP_ID,
            cookie: true,
            xfbml: true,
            version: 'v21.0',
          });
          fbInitialized = true;
        }
        setSdkReady(true);
        resolve();
      };

      if (window.FB) {
        doInit();
        return;
      }

      window.fbAsyncInit = doInit;

      if (!document.getElementById('facebook-jssdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Falha ao carregar Facebook SDK'));
        document.body.appendChild(script);
      }
    });
  }, []);

  const startEmbeddedSignup = useCallback(async () => {
    setLoading(true);
    sessionInfoRef.current = null;

    if (!META_CONFIG_ID) {
      setLoading(false);
      const err = new Error(
        'REACT_APP_META_CONFIG_ID nao definido no .env. Configure o Config ID do Embedded Signup.'
      );
      console.error('[CLOUD-API]', err.message);
      throw err;
    }

    try {
      await loadSDK();

      return new Promise((resolve, reject) => {
        window.FB.login(
          (response) => {
            setLoading(false);

            if (response.authResponse) {
              const code = response.authResponse.code;
              const sessionInfo = sessionInfoRef.current || {};

              resolve({
                code,
                phone_number_id: sessionInfo.phone_number_id || '',
                waba_id: sessionInfo.waba_id || '',
              });
            } else {
              // Meta retorna error_code/error_message ou status quando o
              // popup falha por motivo técnico (config inválida, app não
              // aprovado, etc). Sem isso o usuário/suporte recebia sempre
              // "cancelado pelo usuário", mascarando o motivo real.
              const errMsg =
                response.error_message ||
                response.status ||
                'Autorização cancelada pelo usuário.';
              reject(new Error(errMsg));
            }
          },
          {
            config_id: META_CONFIG_ID,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: '',
              sessionInfoVersion: '3',
            },
          }
        );
      });
    } catch (err) {
      setLoading(false);
      throw err;
    }
  }, [loadSDK]);

  return {
    sdkReady,
    loading,
    loadSDK,
    startEmbeddedSignup,
  };
}
